// components/AvatarUploader/AvatarUploader.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Camera } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Componente para subir y gestionar avatares
 */
export const AvatarUploader = ({ 
  currentAvatar = '', 
  onAvatarChange, 
  size = 'lg',
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(currentAvatar);
  const fileInputRef = useRef(null);

  // Sincronizar preview con currentAvatar cuando cambie externamente
  useEffect(() => {
    setPreview(currentAvatar);
  }, [currentAvatar]);

  const handleFileSelect = (file) => {
    console.log('📁 Archivo seleccionado:', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified
    });

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      console.error('Please select an image file');
      return;
    }
    
    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB en bytes
    if (file.size > maxSize) {
      console.error('File size must be less than 5MB');
      return;
    }

    // Limpiar preview anterior inmediatamente
    setPreview('');
    
    // Convertir a base64 y enviar al parent
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      console.log('📷 Imagen convertida a base64:', {
        size: result.length,
        type: file.type,
        preview: result.substring(0, 50) + '...'
      });
      
      setPreview(result);
      onAvatarChange(result); // base64 string
    };
    
    reader.onerror = (error) => {
      console.error('❌ Error al leer archivo:', error);
    };
    
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('🎯 Archivo desde input:', file.name);
      handleFileSelect(file);
      // Limpiar el input para permitir seleccionar el mismo archivo otra vez
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAvatar = () => {
    console.log('🗑️ Removiendo avatar');
    setPreview('');
    onAvatarChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-20 h-20', 
    lg: 'w-24 h-24',
    xl: 'w-32 h-32'
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`
          ${sizeClasses[size]} 
          border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 
          ${isDragging 
            ? 'border-purple-400 bg-purple-400/10' 
            : preview 
              ? 'border-white/20 bg-white/5' 
              : 'border-white/30 bg-white/10 hover:border-purple-400 hover:bg-purple-400/5'
          }
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt="Avatar"
              className="w-full h-full object-cover rounded-xl"
            />
            {/* Remove button */}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                removeAvatar();
              }}
              variant="ghost"
              size="sm"
              className="absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full bg-red-500 hover:bg-red-600 text-white"
            >
              <X className="w-3 h-3" />
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-full text-gray-400">
            <Camera className="w-6 h-6 mb-1" />
            <span className="text-xs text-center">Upload</span>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default AvatarUploader;